import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  CircularProgress, 
  Button, 
  Alert, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Collapse,
  ListItemButton
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '../contexts/AuthContext';
import { useUserSession } from '../contexts/UserContext';
import authApi from '../services/authApi';
import { sharePointService } from '../services/sharePointService';
import SharePointReport from './SharePoint/SharePointReport';
import { useNavigate, useLocation } from 'react-router-dom';
import SharePointStats from './SharePoint/SharePointStats';
import NavigationDrawer from './Navigation/NavigationDrawer';
import FileManagement from './FileManagement';
import FileUsageReport from './FileUsageReport';
import ExternalSharingReport from './Reports/ExternalSharingReport';
import SecurityAuditReportDW from './SecurityAuditReportDW';
import SharePointConfig from './SharePoint/SharePointConfig';
import UserManagement from './UserManagement/UserManagement';
import ETLMonitoring from './ETLMonitoring';
import ETLStats from './SharePoint/ETLStats';
import StorageGrowthReport from './Reports/StorageGrowthReport';
import PermissionScopeReport from './Reports/PermissionScopeReport';

// Navigation sections configuration
const DRAWER_WIDTH = 200;
const COLLAPSED_DRAWER_WIDTH = 64;

// Add brand colors
const BRAND_COLOR = '#ffffff';
const BRAND_COLOR_FADED = '#ffffff';

const SECTIONS = [
  { 
    id: 'overview', 
    label: 'Overview', 
    icon: <DashboardIcon />, 
    component: SharePointStats 
  },
  { 
    id: 'security', 
    label: 'Security Report', 
    icon: <SecurityIcon />, 
    component: SharePointReport 
  },
  { 
    id: 'file-management', 
    label: 'File Management', 
    icon: <StorageIcon />, 
    component: FileManagement 
  },
  { 
    id: 'file-usage', 
    label: 'File Usage Report', 
    icon: <StorageIcon />, 
    component: FileUsageReport 
  },
  {
    id: 'permission-scope',
    label: 'Permission scope report',
    icon: <GroupIcon />,
    component: PermissionScopeReport
  },
  { 
    id: 'external-sharing', 
    label: 'External Sharing Risks', 
    icon: <ShareIcon />, 
    component: ExternalSharingReport 
  },
  { 
    id: 'security-audit', 
    label: 'Security Audit Report', 
    icon: <SecurityIcon />, 
    component: SecurityAuditReportDW 
  },
  { 
    id: 'sharepoint-config', 
    label: 'My SharePoint Configuration', 
    icon: <SettingsIcon />, 
    component: SharePointConfig 
  },
  { 
    id: 'user-management', 
    label: 'User Management', 
    icon: <GroupIcon />, 
    component: UserManagement 
  },
  {
    id: 'etl-stats',
    label: 'ETL Stats',
    icon: <StorageIcon />,
    component: ETLStats
  },
  {
    id: 'etl-monitor',
    label: 'ETL Monitor', 
    icon: <StorageIcon />, 
    component: ETLMonitoring 
  },
  { 
    id: 'storage-growth', 
    label: 'Storage Growth', 
    icon: <StorageIcon />, 
    component: StorageGrowthReport 
  }
];

const MENU_ITEMS = {
  reports: {
    id: 'reports',
    label: 'Reports',
    icon: <AssessmentIcon />,
    items: [
      { id: 'external-sharing', label: 'External Sharing Risks', icon: <ShareIcon />, path: '/external-sharing-report' },
      { id: 'security-audit', label: 'Security Audit Report', icon: <SecurityIcon />, path: '/security-audit-report-dw' },
      { id: 'file-management', label: 'File Management', icon: <StorageIcon />, path: '/file-management' },
      { id: 'file-usage', label: 'File Usage Report', icon: <StorageIcon />, path: '/file-usage-report' }
    ]
  },
  configuration: {
    id: 'configuration',
    label: 'Configuration',
    icon: <SettingsIcon />,
    items: [
      { id: 'sharepoint', label: 'My SharePoint Configuration', icon: <SettingsIcon />, path: '/sharepoint-config' },
      { id: 'users', label: 'User Management', icon: <GroupIcon />, path: '/user-management', roles: ['System Admin'] },
      { id: 'etl-ops', label: 'ETL Operations', icon: <StorageIcon />, path: '/admin/etl-operations', roles: ['System Admin'] },
      { id: 'etl-monitor', label: 'ETL Monitor', icon: <StorageIcon />, path: '/etl-monitor' }
    ]
  }
};

const pathToSection = {
  '/dashboard': 'overview',
  '/security-report': 'security',
  '/file-management': 'file-management',
  '/file-usage-report': 'file-usage',
  '/permission-scope-report': 'permission-scope',
  '/user-scope-report': 'permission-scope',
  '/external-sharing-report': 'external-sharing',
  '/security-audit-report-dw': 'security-audit',
  '/sharepoint-config': 'sharepoint-config',
  '/user-management': 'user-management',
  '/etl-stats': 'etl-stats',
  '/etl-monitor': 'etl-monitor',
  '/storage-growth': 'storage-growth'
};

const Dashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { userSession, loading: userSessionLoading } = useUserSession();
  const [hasSharePointConfig, setHasSharePointConfig] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(
    pathToSection[window.location.pathname] || 'overview'
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Listen to URL changes and update activeSection
  useEffect(() => {
    console.log('Dashboard location changed:', location.pathname);

    const newSection = pathToSection[location.pathname] || 'overview';
    console.log('Setting activeSection to:', newSection);
    setActiveSection((prev) => (prev === newSection ? prev : newSection));
  }, [location.pathname]);

  useEffect(() => {
    const checkSharePointConfig = async () => {
      console.log('Checking SharePoint config - User Session:', userSession);
      console.log('Is Authenticated:', isAuthenticated);
      
      if (isAuthenticated && userSession?.email) {
        console.log('Checking config for user:', userSession.email);
        setCheckingConfig(true);
        try {
          const token = authApi.getToken();
          const config = await sharePointService.getConfig(token);
          console.log('SharePoint config found:', !!config);
          setHasSharePointConfig(!!config);
        } catch (error) {
          console.error('Error checking SharePoint config:', error);
          setHasSharePointConfig(false);
        } finally {
          setCheckingConfig(false);
        }
      } else {
        console.log('Not checking config - missing auth or email');
      }
    };

    checkSharePointConfig();
  }, [isAuthenticated, userSession?.email]);

  if (authLoading || userSessionLoading || checkingConfig) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Configure SharePoint account first if none exists
  if (isAuthenticated && !hasSharePointConfig) {
    return (
      <Container sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Welcome to SecurePoint IQ! To get started, please configure your SharePoint account.
          </Alert>
          <Typography variant="h5" component="h1" gutterBottom>
            Welcome, {userSession?.name || user?.name || 'there'}!
          </Typography>
          <Typography variant="body1" paragraph>
            Before you can use the dashboard, you need to configure your SharePoint account.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/sharepoint-config')}
          >
            Configure SharePoint
          </Button>
        </Paper>
      </Container>
    );
  }

  const renderActiveSection = () => {
    const ActiveComponent = SECTIONS.find(section => section.id === activeSection)?.component;
    
    if (!ActiveComponent) {
      return <Typography>Section not found</Typography>;
    }
    
    return <ActiveComponent />;
  };

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleMenuClick = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? '' : menuId);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#ffffff',
      }}
    >
      {/* Navigation Drawer */}
      <NavigationDrawer 
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        navigate={navigate}
      />
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          pt: 2,
          backgroundColor: '#ffffff',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            {SECTIONS.find(s => s.id === activeSection)?.label || 'Dashboard'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
        
        {/* Main content area */}
        <Box>
          {renderActiveSection()}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard; 